"use strict";

import React from "react";
import { RawScore, WrappedText } from "./components.js";
import { simpleTitleCase } from "./shared.js";

const PUZZLE_MAIN = "main_puzzle";
const PUZZLE_MINI = "mini_puzzle";
const PUZZLE_INDV = "standalone_piece";
const PUZZLE_IDVL = "standalone_labeled";

export const PUZZLE_CONFIG = ((d) => {
  // calculate derived values
  let halfLineWidth = d.lineWidth / 2;
  d.clipRadius = d.radius + d.lineWidth;
  d.altClipRadius = d.altRadius + d.lineWidth;
  d.clippedSideLen =
    d.sideLen -
    Math.sqrt(d.clipRadius * d.clipRadius - halfLineWidth * halfLineWidth);
  d.altClippedSideLen =
    d.sideLen -
    Math.sqrt(
      d.altClipRadius * d.altClipRadius - halfLineWidth * halfLineWidth,
    );
  d.shortenedSideLen = d.sideLen - halfLineWidth;
  return d;
})({
  sideLen: 50,
  radius: 25,
  altRadius: 15, // for `PUZZLE_IDVL` pieces
  lineWidth: 4,
  textPadding: 4,
});

function SidePiece({ className, transform, useAlt }) {
  let [SS, CS, CR] = useAlt
    ? [
        PUZZLE_CONFIG.shortenedSideLen,
        PUZZLE_CONFIG.altClippedSideLen,
        PUZZLE_CONFIG.altClipRadius,
      ]
    : [
        PUZZLE_CONFIG.shortenedSideLen,
        PUZZLE_CONFIG.clippedSideLen,
        PUZZLE_CONFIG.clipRadius,
      ];
  // see https://www.w3.org/TR/SVG/paths.html#PathDataEllipticalArcCommands for arc syntax
  return (
    <path
      className={className}
      transform={transform}
      d={`M0,0 L${SS},0 L${SS},${CS} A${CR},${CR},0,0,0,${CS},${SS} L0,${SS} Z`}
    />
  );
}

function CenterPiece({ className, transform }) {
  return (
    <circle
      className={className}
      transform={transform}
      cx="0"
      cy="0"
      r={PUZZLE_CONFIG.radius}
    />
  );
}

function MissingPiece({ className, transform }) {
  let R = PUZZLE_CONFIG.radius * 0.9;
  return (
    <rect
      className={className}
      transform={transform}
      x={-R}
      y={-R / 3}
      width={2 * R}
      height={(2 * R) / 3}
    />
  );
}

function MeasurePiece({ className, transform }) {
  let R = PUZZLE_CONFIG.radius;
  return (
    <rect
      className={className}
      transform={transform}
      x={-R}
      y={-R}
      width={2 * R}
      height={2 * R}
    />
  );
}

export function PositionedPiece({ item, result, context = PUZZLE_INDV }) {
  let P = PUZZLE_CONFIG.textPadding;
  let S =
    context === PUZZLE_INDV || context === PUZZLE_IDVL
      ? PUZZLE_CONFIG.sideLen
      : PUZZLE_CONFIG.sideLen * 2;

  let Piece = item.slug === "instruction" ? CenterPiece : SidePiece;
  let pieceTransform, labelTransform, labelAlignX, labelAlignY;
  let _key = item._level === "measures" ? "measure" : item.slug;
  switch (_key) {
    case "instruction":
    // ctr
    case "_supplemental":
    case "measure":
      if (_key === "_supplemental") Piece = MissingPiece;
      else if (_key === "measure") Piece = MeasurePiece;
      pieceTransform = `translate(${S / 2},${S / 2})`;
      labelTransform = `translate(${S / 2},${S / 2})`;
      (labelAlignX = "middle"), (labelAlignY = "center");
      break;
    case "leaders": // nw
      pieceTransform = `translate(0,0) rotate(0)`;
      labelTransform = `translate(${P},${P})`;
      (labelAlignX = "start"), (labelAlignY = "top");
      break;
    case "teachers": // ne
      pieceTransform = `translate(${S},0) rotate(90)`;
      labelTransform = `translate(${S - P},${P})`;
      (labelAlignX = "end"), (labelAlignY = "top");
      break;
    case "families": // se
      pieceTransform = `translate(${S},${S}) rotate(180)`;
      labelTransform = `translate(${S - P},${S - P})`;
      (labelAlignX = "end"), (labelAlignY = "bottom");
      break;
    case "environment": // sw
      pieceTransform = `translate(0,${S}) rotate(270)`;
      labelTransform = `translate(${P},${S - P})`;
      (labelAlignX = "start"), (labelAlignY = "bottom");
      break;
    default:
      //throw Error(`Unknown item: ${item._level}/${item.slug}`)
      return null; // (helps facilitate rending BEL as 5e during development, etc.)
  }

  let piece = (
    <Piece
      className={`rank-${result.code}`}
      transform={pieceTransform}
      useAlt={context === PUZZLE_IDVL}
    />
  );
  if (context === PUZZLE_MAIN) {
    let lines = item.name.split(" ");
    let yAdj = "0"; // HACK: we handle vertical alignment ourselves :-/
    // see https://www.safaribooksonline.com/library/view/svg-text-layout/9781491933817/ch04.html#position-tspan-vertical-section
    if (labelAlignY === "bottom") {
      yAdj = `${-1.2 * lines.length}em`;
    } else if (labelAlignY === "center") {
      yAdj = `${(-1.2 * lines.length) / 2}em`;
    }
    // HT: http://stackoverflow.com/a/23272711/179583
    let label = (
      <text
        className={`rank-${result.code}-text`}
        transform={labelTransform}
        y={yAdj}
      >
        {lines.map((str, i) => (
          <tspan key={i} textAnchor={labelAlignX} x="0" dy="1.2em">
            {str}
          </tspan>
        ))}
      </text>
    );
    return (
      <g>
        {piece}
        {label}
      </g>
    );
  } else if (context === PUZZLE_IDVL) {
    let label = (
      <text
        className={`rank-${result.code}-text`}
        x={S / 2}
        y={S / 2}
        textAnchor="middle"
        dominantBaseline="central"
      >
        <RawScore result={result} Wrapper="tspan" />
      </text>
    );
    return (
      <g>
        {piece}
        {label}
      </g>
    );
  } else {
    return piece;
  }
}

export function Puzzle({
  essentials,
  size = 250,
  labels = true,
  generateLink = null,
  ...other
}) {
  let pieceContext = labels ? PUZZLE_MAIN : PUZZLE_MINI;
  let _essentials = essentials.overall._children;
  return (
    <svg
      className="overall-rep puzzle"
      width={size}
      height={size}
      viewBox="0 0 100 100"
    >
      {_essentials.map((d) => (
        <a key={d.slug} xlinkHref={generateLink ? generateLink(d) : null}>
          <PositionedPiece
            item={d}
            result={d._results.latest}
            context={pieceContext}
          />
        </a>
      ))}
    </svg>
  );
}

export function PuzzlePiece({ item, size = 10, score_label = false }) {
  return (
    <svg
      className="individual-rep puzzle-piece"
      width={size}
      height={size}
      viewBox="0 0 50 50"
    >
      <PositionedPiece
        item={item}
        result={item._results.latest}
        context={score_label ? PUZZLE_IDVL : PUZZLE_INDV}
      />
    </svg>
  );
}

export function PuzzleLegend({ allRanks }) {
  return (
    <ul className="puzzle-legend">
      {allRanks.map((d) => (
        <li key={d.code}>
          <span className={`swatch rank-${d.code}-cell`} />{" "}
          {simpleTitleCase(d.long_label)}
        </li>
      ))}
    </ul>
  );
}
